import { chaptersOf, faqOf, formatTimestamp, learnItemsOf, summaryOf } from '@/site/lib/enrichment'
import type { Tutorial } from '@/site/lib/types'

/**
 * "What You'll Learn" is hidden for now (2026-10-02): its content is templated (the about text again plus generic
 * bullets on ~89% of tutorials), so it adds nothing for readers or search. The data is still generated
 * (tutorials:seo-enrichment); turn this back on once the bullets are real, e.g. written by the Claude assistant.
 */
const SHOW_WHAT_YOULL_LEARN = false

interface Props {
  tutorial: Pick<Tutorial, 'metadata' | 'video'>
  /** Play the page's video from this many seconds (chapter links). Without it they open YouTube. */
  onSeek?: (seconds: number) => void
}

/**
 * "What You'll Learn", "Chapters" and "Common Questions" (v1 PR #5), from the tutorial's video_chapters and SEO
 * enrichment metadata. Server-rendered, so search engines read it. Renders nothing when there's none.
 */
export default function TutorialEnrichment({ tutorial, onSeek }: Props) {
  const chapters = chaptersOf(tutorial)
  const faq = faqOf(tutorial)
  const learn = SHOW_WHAT_YOULL_LEARN ? learnItemsOf(tutorial) : []
  const summary = SHOW_WHAT_YOULL_LEARN ? summaryOf(tutorial) : ''
  const youtubeId = tutorial.video?.youtube_id

  if (!chapters.length && !learn.length && !summary && !faq.length) return null

  return (
    <div className="flex flex-col gap-6">
      <div className={summary || learn.length > 0 ? 'grid gap-6 md:grid-cols-2' : 'grid gap-6'}>
        {(summary || learn.length > 0) && (
          <section aria-labelledby="learn" className="rounded-3xl bg-qt-soft p-5 md:p-7">
            <h2 id="learn" className="text-2xl font-extrabold md:text-[28px]">
              What You’ll Learn
            </h2>
            {summary && <p className="mt-3 leading-7">{summary}</p>}
            {learn.length > 0 && (
              <ul className="mt-4 flex list-disc flex-col gap-2 pl-5 leading-6">
                {learn.map((item, i) => (
                  <li key={`${item}-${i}`}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        )}

        {chapters.length > 0 && (
          <section aria-labelledby="chapters" className="rounded-3xl border-2 border-qt-line p-5 md:p-7">
            <h2 id="chapters" className="text-2xl font-extrabold md:text-[28px]">
              Chapters
            </h2>
            <ol className="mt-4 flex flex-col gap-3">
              {chapters.map((chapter) => (
                <li key={`${chapter.position}-${chapter.name}`} className="flex gap-3 leading-6">
                  {youtubeId ? (
                    <a
                      href={`https://www.youtube.com/watch?v=${youtubeId}&t=${chapter.startOffset}s`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        if (!onSeek) return
                        e.preventDefault()
                        onSeek(chapter.startOffset)
                      }}
                      className="font-extrabold whitespace-nowrap text-qt-primary tabular-nums underline underline-offset-2 hover:no-underline"
                      aria-label={`Play from ${formatTimestamp(chapter.startOffset)}: ${chapter.name}`}
                    >
                      {formatTimestamp(chapter.startOffset)}
                    </a>
                  ) : (
                    <span className="font-extrabold whitespace-nowrap text-qt-primary tabular-nums">{formatTimestamp(chapter.startOffset)}</span>
                  )}
                  <span className="font-bold">{chapter.name}</span>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      {faq.length > 0 && (
        <section aria-labelledby="faq" className="rounded-3xl bg-qt-soft p-5 md:p-7">
          <h2 id="faq" className="text-2xl font-extrabold md:text-[28px]">
            Common Questions
          </h2>
          <div className="mt-4 flex flex-col gap-4">
            {faq.map((item) => (
              <div key={item.question}>
                <h3 className="font-extrabold">{item.question}</h3>
                <p className="mt-1 leading-7">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
